import React, { useCallback, useMemo, useState } from "react";

import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import {
  Box,
  Button,
  ButtonProps,
  Checkbox,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  SxProps,
  Theme
} from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useModals } from "hooks";
import { IApiServiceError, useInvestorsSelector, useReportsActions } from "store";
import { ReportContext, Report as ReportType } from "store/types";

import { evaluateCondition, useFilteredReports } from "./use-filtered-reports";
import { ClientProcedureHook, usePrePostReportHook } from "./usePrePostReportHook";

interface ExtendedMenuItemProps extends Omit<MenuItemProps, "sx"> {
  sx?: SxProps<Theme>;
  listItemIconSx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
  listItemTextSx?: SxProps<Theme>;
}

type ButtonOrIconButtonProps = Partial<
  Omit<ButtonProps, "size" | "onClick"> & Omit<IconButtonProps, "size" | "onClick">
> & {
  size?: "small" | "medium" | "large" | "sm" | "md" | "lg";
};

interface Props {
  context: ReportContext;
  group: string;
  reports?: ReportType[];
  loading?: boolean;
  buttonProps?: ButtonOrIconButtonProps;
  menuItemProps?: ExtendedMenuItemProps;
  icon?: React.ReactNode;
  iconButton?: boolean;
  tooltipTitle?: string;
  getReportsByGroup?: (groupName: string) => ReportType[];
}

/**
 * ReportButton component
 *
 * Requires a group name and fetches/filters reports based on the provided context.
 * Can also accept pre-filtered reports directly via reports and loading props.
 * - Single report: Direct button that generates on click
 * - Multiple reports: Menu with a checkbox per report; "Download" runs selected reports in parallel
 */
export const ReportButton: React.FC<Props> = ({
  group,
  context,
  reports: providedReports,
  loading: providedLoading,
  buttonProps,
  menuItemProps,
  icon,
  iconButton = true,
  tooltipTitle,
  getReportsByGroup
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [checkedReportIds, setCheckedReportIds] = useState<Set<number>>(() => new Set());
  const [generating, setGenerating] = useState(false);
  const { errorModal } = useModals();
  const { generateReportAction } = useReportsActions();
  const investors = useInvestorsSelector();
  const prePostReport = usePrePostReportHook();

  const clientProcedures = useMemo<Record<string, ClientProcedureHook>>(
    () => ({
      prepost_report: prePostReport
    }),
    [prePostReport]
  );

  // Fetch and filter reports based on group and context
  // If reports/loading are provided, they override
  // If getReportsByGroup is provided, use it; otherwise useFilteredReports will return empty array
  const { reports: fetchedReports } = useFilteredReports(group, context, getReportsByGroup);

  // Use provided reports or fetched ones
  const reports = providedReports ?? fetchedReports;
  const loading = providedLoading ?? false;

  // Use default icon if none provided
  const displayIcon = icon || <AutoGraphOutlinedIcon />;

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setCheckedReportIds(new Set());
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setCheckedReportIds(new Set());
    setAnchorEl(event.currentTarget);
  };

  const toggleReportChecked = useCallback((reportId: number) => {
    setCheckedReportIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) {
        next.delete(reportId);
      } else {
        next.add(reportId);
      }
      return next;
    });
  }, []);

  const handleReportClick = useCallback(
    async (report: ReportType) => {
      try {
        // Build initial context: row data + matching investor from store
        let accumulatedContext: ReportContext = { ...context };
        if (context.row) {
          accumulatedContext.row = context.row as Record<string, unknown>;
        }

        // Derive investor inside the callback to avoid stale closure
        const investorName = (context.row as { investor?: string } | undefined)?.investor;
        const investorFromStore = investorName ? investors.find((inv) => inv.name === investorName) : undefined;

        if (investorFromStore) {
          accumulatedContext.investor = {
            account: investorFromStore.additional_information ?? undefined,
            bank_details: investorFromStore.bank_details ?? undefined
          };
          accumulatedContext.investor_id = investorFromStore.id;
        }

        const triggers = report.configuration?.client_triggers;
        if (triggers?.length) {
          for (let i = 0; i < triggers.length; i++) {
            const trigger = triggers[i];
            const run = !trigger.condition || evaluateCondition(trigger.condition, accumulatedContext);
            if (!run) continue;

            const procedureName = trigger.execute.replace(/\(.*\)/, "").trim();
            const procedure = clientProcedures[procedureName];
            if (!procedure) {
              console.warn(`Unknown client procedure: ${procedureName}`);
              continue;
            }
            const output = await procedure(accumulatedContext);
            accumulatedContext = { ...accumulatedContext, ...output };
          }
        }

        // Generate with final context (including any procedure outputs e.g. bank_percentages)
        await generateReportAction({ reportId: report.id, ...accumulatedContext });
      } catch (error) {
        if ((error as Error).message !== "cancelled") {
          errorModal({ msg: (error as IApiServiceError).message ?? (error as Error).message });
        }
      }
    },
    [context, investors, clientProcedures, generateReportAction, errorModal]
  );

  const handleGenerateSelected = useCallback(async () => {
    const selected = reports.filter((r) => checkedReportIds.has(r.id));
    if (!selected.length) return;

    setGenerating(true);

    await Promise.allSettled(selected.map(handleReportClick));

    handleClose();
    setGenerating(false);
  }, [reports, checkedReportIds, handleReportClick, handleClose]);

  const handleSingleReport = useCallback(
    async (report: ReportType) => {
      setGenerating(true);
      await handleReportClick(report);
      setGenerating(false);
    },
    [handleReportClick]
  );

  // Merge default styles with any provided menuItemProps.sx
  const menuItemStyles = menuItemProps?.sx ? { ...defaultMenuItemStyles, ...menuItemProps.sx } : defaultMenuItemStyles;

  // Extract sx from menuItemProps to avoid double application
  const { sx: _sx, ...restMenuItemProps } = menuItemProps || {};

  // Extract icon and text styles from menuItemProps if provided
  const listItemIconStyles = menuItemProps?.listItemIconSx
    ? { ...defaultListItemIconStyles, ...menuItemProps.listItemIconSx }
    : defaultListItemIconStyles;

  const iconStyles = menuItemProps?.iconSx ? { ...defaultIconStyles, ...menuItemProps.iconSx } : defaultIconStyles;

  const listItemTextStyles = menuItemProps?.listItemTextSx
    ? { ...defaultListItemTextStyles, ...menuItemProps.listItemTextSx }
    : defaultListItemTextStyles;

  const open = Boolean(anchorEl);

  // If no reports available, don't render anything
  if (!reports.length && !loading) {
    return null;
  }

  // Single report - direct generation
  if (reports.length === 1) {
    const report = reports[0];

    if (iconButton) {
      const tooltip = tooltipTitle || report.configuration?.tooltip || report.name || report.description;
      const iconButtonElement = (
        <IconButton
          onClick={() => handleSingleReport(report)}
          disabled={generating || loading}
          {...(buttonProps as IconButtonProps)}
        >
          {displayIcon}
        </IconButton>
      );

      if (tooltip) {
        return (
          <MaterialTooltip title={tooltip} isShow>
            {iconButtonElement}
          </MaterialTooltip>
        );
      }

      return iconButtonElement;
    }
    return (
      <Button
        onClick={() => handleSingleReport(report)}
        disabled={generating || loading}
        {...(buttonProps as ButtonProps)}
      >
        {report.name}
      </Button>
    );
  }

  // Multiple reports - show menu
  if (iconButton) {
    const tooltip = tooltipTitle || "Reports";
    const iconButtonElement = (
      <IconButton onClick={handleClick} disabled={loading} {...(buttonProps as IconButtonProps)}>
        {displayIcon}
      </IconButton>
    );

    const wrappedButton = tooltip ? (
      <MaterialTooltip title={tooltip} isShow>
        {iconButtonElement}
      </MaterialTooltip>
    ) : (
      iconButtonElement
    );

    return (
      <>
        {wrappedButton}
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <ProgressOverlay inProgress={generating || loading}>
            {reports.map((report) => (
              <MenuItem
                key={report.id}
                onClick={() => toggleReportChecked(report.id)}
                sx={menuItemStyles}
                {...restMenuItemProps}
              >
                <Checkbox
                  edge="start"
                  checked={checkedReportIds.has(report.id)}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleReportChecked(report.id)}
                />
                <ListItemIcon sx={listItemIconStyles}>
                  {React.cloneElement(displayIcon as React.ReactElement, {
                    sx: iconStyles
                  })}
                </ListItemIcon>
                <ListItemText sx={listItemTextStyles}>{report.name}</ListItemText>
              </MenuItem>
            ))}
            {checkedReportIds.size > 0 ? (
              <Box sx={{ px: 1.5, py: 1, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => handleGenerateSelected()}
                  disabled={generating || loading}
                  sx={{ maxWidth: 100 }}
                >
                  Download
                </Button>
              </Box>
            ) : null}
          </ProgressOverlay>
        </Menu>
      </>
    );
  }

  return (
    <>
      <Button onClick={handleClick} disabled={loading} {...(buttonProps as ButtonProps)}>
        Reports
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <ProgressOverlay inProgress={generating || loading}>
          {reports.map((report) => (
            <MenuItem
              key={report.id}
              onClick={() => toggleReportChecked(report.id)}
              sx={menuItemStyles}
              {...restMenuItemProps}
            >
              <Checkbox
                edge="start"
                checked={checkedReportIds.has(report.id)}
                tabIndex={-1}
                disableRipple
                size="small"
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleReportChecked(report.id)}
              />
              <ListItemIcon sx={listItemIconStyles}>
                {React.cloneElement(displayIcon as React.ReactElement, {
                  sx: iconStyles
                })}
              </ListItemIcon>
              <ListItemText sx={listItemTextStyles}>{report.name}</ListItemText>
            </MenuItem>
          ))}
          {checkedReportIds.size > 0 ? (
            <Box sx={{ px: 1.5, py: 1, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={() => handleGenerateSelected()}
                disabled={generating || loading}
                sx={{ maxWidth: 100 }}
              >
                Download
              </Button>
            </Box>
          ) : null}
        </ProgressOverlay>
      </Menu>
    </>
  );
};

// Default menu item styles
const defaultMenuItemStyles = {
  fontSize: "15px",
  color: "#071F35",
  "&:hover": {
    backgroundColor: "#ECF1F8"
  }
};

// Default ListItemIcon styles
const defaultListItemIconStyles = {
  minWidth: "36px"
};

// Default icon styles
const defaultIconStyles = {
  fontSize: "20px",
  color: "#071F35"
};

// Default ListItemText styles
const defaultListItemTextStyles = {
  "& .MuiListItemText-primary": { fontSize: "15px" }
};
